import type { TravelerResponseDto } from '@bandido/shared/dtos';
import type { TravelerCaptureCandidate } from '@bandido/shared/types';
import { normalizeDocumentNumber } from '@bandido/shared/helpers';

const words = (value?: string | null) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const similarWord = (a: string, b: string) => {
  if (a === b) return true;
  if (Math.min(a.length, b.length) < 5 || Math.abs(a.length - b.length) > 1) return false;
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(row[j - 1] + 1, previous[j] + 1, previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    previous = row;
  }
  return previous[b.length] <= 1;
};

const similarNames = (a?: string | null, b?: string | null) => {
  const left = words(a),
    right = words(b);
  const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
  return shorter.length > 0 && shorter.every((word) => longer.some((other) => similarWord(word, other)));
};

/** Suggestions use only the authorized case roster and never select or merge a person. */
export const captureTravelerMatches = (candidate: TravelerCaptureCandidate, travelers: TravelerResponseDto[]) =>
  travelers
    .flatMap((traveler) => {
      const documentMatch = candidate.documents.some((document) => {
        const number = normalizeDocumentNumber(document.number.value ?? '');
        return (
          number.length >= 3 &&
          traveler.documents.some(
            (existing) => existing.type === document.type.value && normalizeDocumentNumber(existing.number) === number,
          )
        );
      });
      const nameMatch =
        similarNames(candidate.fields.firstName?.value, traveler.firstName) &&
        similarNames(candidate.fields.lastName?.value, traveler.lastName);
      if (!documentMatch && !nameMatch) return [];
      const birthDate = candidate.fields.birthDate?.value;
      return [
        {
          traveler,
          documentMatch,
          birthDateMatch: Boolean(birthDate && birthDate === traveler.birthDate),
          birthDateConflict: Boolean(birthDate && traveler.birthDate && birthDate !== traveler.birthDate),
        },
      ];
    })
    .sort(
      (a, b) =>
        Number(b.documentMatch) - Number(a.documentMatch) || Number(b.birthDateMatch) - Number(a.birthDateMatch),
    );
